import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  HelpCircle, Search, MessageCircle, ArrowLeft, Clock, ChevronRight,
  ShoppingCart, Package, Users, BarChart3, Settings, RefreshCcw,
  CreditCard, BookOpen, Star, AlertCircle, CheckCircle2, Lightbulb,
  Zap,
} from "lucide-react";
import { LegalFooter } from "./privacy-policy";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ─── Article Data ────────────────────────────────────────────────────────────

type Article = {
  id: string;
  category: string;
  title: string;
  description: string;
  readTime: string;
  popular?: boolean;
  content: Section[];
};

type Section =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "steps"; items: string[] }
  | { type: "tips"; items: string[] }
  | { type: "warning"; text: string }
  | { type: "note"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

const ARTICLES: Article[] = [
  // ── GETTING STARTED ────────────────────────────────────────────────────────
  {
    id: "gs-setup",
    category: "Getting Started",
    title: "Setting up your store for the first time",
    description: "A complete walkthrough of creating your account, configuring your store, and making your first sale.",
    readTime: "5 min",
    popular: true,
    content: [
      { type: "p", text: "When you first sign up for RetailPulse, you get a 10-day free trial with full access — no credit card needed. This guide walks you through everything from signup to your first sale." },
      { type: "h2", text: "Step 1: Create your account" },
      { type: "steps", items: [
        "Go to retailpulsepos.com and click Start Free Trial.",
        "Enter your store name, your name, email address, and a strong password.",
        "Choose your business type (e.g. Supermarket, Fashion, Salon). This customises your dashboard, menu items, and reports for your industry.",
        "Click Create Account — your store is ready instantly.",
      ]},
      { type: "h2", text: "Step 2: Configure your store settings" },
      { type: "steps", items: [
        "Go to Settings from the sidebar.",
        "Update your store address, phone number, and email — these appear on receipts.",
        "Set your currency (₦ Naira is the default).",
        "Upload your store logo if you have one.",
      ]},
      { type: "h2", text: "Step 3: Add your products" },
      { type: "p", text: "Before you can make a sale you need at least one product. Go to Inventory → Add Product and fill in the name, selling price, cost price, and opening stock quantity." },
      { type: "tips", items: [
        "Cost price is used to calculate your profit. If you leave it blank, profit reporting will be inaccurate.",
        "Set a low-stock threshold — you'll see a warning on your dashboard when stock falls below it.",
      ]},
      { type: "h2", text: "Step 4: Make your first sale" },
      { type: "steps", items: [
        "Click New Sale in the sidebar.",
        "Search for a product and click it to add it to the cart.",
        "Adjust quantities if needed.",
        "Select a payment method (Cash, Card, or Transfer).",
        "Click Confirm Sale — a receipt is generated automatically.",
      ]},
      { type: "note", text: "Your dashboard updates in real time as you make sales. Revenue, top products, and expense charts all reflect today's data." },
    ],
  },
  {
    id: "gs-dashboard",
    category: "Getting Started",
    title: "Understanding your dashboard",
    description: "What each card and chart means and how to use your dashboard to make daily decisions.",
    readTime: "3 min",
    content: [
      { type: "p", text: "Your dashboard gives you a live view of your business performance. The layout adapts to your business type — a supermarket sees different metrics than a salon." },
      { type: "h2", text: "Key metric cards" },
      { type: "table", headers: ["Card", "What it shows"], rows: [
        ["Today's Revenue", "Total sales amount for today"],
        ["Today's Profit", "Revenue minus cost of goods sold today"],
        ["Transactions", "Number of sales completed today"],
        ["Low Stock Alerts", "Products below your set threshold"],
        ["Outstanding Credit", "Total amount owed by credit customers"],
        ["Total Expenses", "Expenses logged today"],
      ]},
      { type: "h2", text: "Charts" },
      { type: "list", items: [
        "Sales Chart — bar chart of daily revenue over the past 7 or 30 days.",
        "Top Products — your best-selling items by quantity or revenue this period.",
        "Expense Breakdown — pie chart of your expense categories.",
        "Payment Method Split — cash vs card vs transfer.",
      ]},
      { type: "h2", text: "Role-based access" },
      { type: "p", text: "Staff accounts do not see financial cards (revenue, profit, expenses). They only see their own sales count and the new sale button. Manager and Admin roles see everything." },
    ],
  },
  {
    id: "gs-team",
    category: "Getting Started",
    title: "Adding your team to RetailPulse",
    description: "How to add staff, set roles, and manage who can access what.",
    readTime: "3 min",
    content: [
      { type: "p", text: "RetailPulse supports multiple staff accounts per store, each with their own login and role-based permissions." },
      { type: "h2", text: "How to add a staff member" },
      { type: "steps", items: [
        "Go to Staff in the sidebar.",
        "Click Add Staff Member.",
        "Enter their full name, email address, and select a role.",
        "Click Save — they can now log in with their email and the default password shown.",
        "Ask them to change their password after first login from their account settings.",
      ]},
      { type: "h2", text: "Roles and permissions" },
      { type: "table", headers: ["Permission", "Admin", "Manager", "Staff"], rows: [
        ["Process sales", "✅", "✅", "✅"],
        ["View inventory", "✅", "✅", "✅"],
        ["Add/edit products", "✅", "✅", "❌"],
        ["View sales reports", "✅", "✅", "Own only"],
        ["View revenue & profit", "✅", "✅", "❌"],
        ["Add expenses", "✅", "✅", "❌"],
        ["Manage staff", "✅", "❌", "❌"],
        ["Access billing", "✅", "❌", "❌"],
        ["Change settings", "✅", "❌", "❌"],
      ]},
      { type: "tips", items: [
        "Give cashiers the Staff role — they can sell without seeing your financials.",
        "Give your floor manager the Manager role so they can handle inventory without touching billing.",
      ]},
    ],
  },

  // ── SALES & POS ─────────────────────────────────────────────────────────────
  {
    id: "sales-process",
    category: "Sales & POS",
    title: "How to process a sale",
    description: "Step-by-step guide to the full POS sale flow from adding items to collecting payment.",
    readTime: "4 min",
    popular: true,
    content: [
      { type: "p", text: "The RetailPulse POS is designed to be fast — most sales take under 30 seconds." },
      { type: "h2", text: "Starting a sale" },
      { type: "steps", items: [
        "Click New Sale in the sidebar.",
        "Search for a product by name or scan its barcode.",
        "Click the product card to add it to the cart.",
        "Adjust the quantity using the + and − buttons in the cart.",
        "Repeat for all items the customer is buying.",
      ]},
      { type: "h2", text: "Applying discounts" },
      { type: "steps", items: [
        "In the cart summary, click the Discount field.",
        "Enter a percentage (e.g. 10 for 10%) or a flat amount.",
        "The total updates automatically.",
      ]},
      { type: "h2", text: "Selecting payment method" },
      { type: "list", items: [
        "Cash — enter the amount tendered and change is calculated automatically.",
        "Card — select Card and confirm payment on your POS terminal.",
        "Transfer — select Transfer for bank transfers or USSD payments.",
        "Credit / Unpaid — if a customer owes you, select Credit and link it to a customer account. It appears in your Unpaid Sales list for follow-up.",
      ]},
      { type: "h2", text: "Completing the sale" },
      { type: "steps", items: [
        "Click Confirm Sale.",
        "A receipt is generated with a unique receipt number (e.g. RCP-00042).",
        "Print, download, or send via SMS to the customer.",
      ]},
      { type: "tips", items: [
        "Link a customer to the sale before confirming to award loyalty points automatically.",
        "Add a tax percentage if your business charges VAT — it's added on top of the subtotal.",
      ]},
    ],
  },
  {
    id: "sales-credit",
    category: "Sales & POS",
    title: "Managing credit and unpaid sales",
    description: "How to record buy-now-pay-later sales and track who owes you money.",
    readTime: "3 min",
    content: [
      { type: "p", text: "Credit sales let you record a transaction even when the customer hasn't paid yet. RetailPulse tracks the outstanding balance so you always know who owes what." },
      { type: "h2", text: "Recording a credit sale" },
      { type: "steps", items: [
        "In the new sale screen, select Credit as the payment method.",
        "You must link a customer — click Select Customer and search for them (or add a new customer).",
        "Confirm the sale. It's recorded in your system but marked as unpaid.",
      ]},
      { type: "h2", text: "Collecting payment later" },
      { type: "steps", items: [
        "Go to Unpaid Sales in the sidebar.",
        "Find the customer's outstanding sale.",
        "Click Mark as Paid and select the payment method received.",
        "The sale moves out of the unpaid list and your revenue updates.",
      ]},
      { type: "warning", text: "Credit sales count as revenue in your reports only when marked as paid. Until then, they appear in Outstanding Credit on your dashboard." },
    ],
  },
  {
    id: "sales-receipt",
    category: "Sales & POS",
    title: "Receipts and SMS notifications",
    description: "How receipts work, how to customise them, and how to send them via SMS.",
    readTime: "3 min",
    content: [
      { type: "p", text: "Every completed sale generates a digital receipt with your store name, the items sold, total, payment method, and a unique receipt number." },
      { type: "h2", text: "Receipt options" },
      { type: "list", items: [
        "View — opens the full receipt in the browser.",
        "Print — sends to your connected printer.",
        "Download — saves as PDF.",
        "Send SMS — sends the receipt summary to the customer's phone number (requires Termii setup).",
      ]},
      { type: "h2", text: "Setting up SMS receipts" },
      { type: "steps", items: [
        "Create a free account at termii.com.",
        "Get your API key from the Termii dashboard.",
        "In RetailPulse, go to Settings → SMS and paste your API key.",
        "Set a sender name (e.g. RETAILPULSE or your store name).",
        "SMS receipts are now active — they'll send automatically when you click Send SMS on any receipt.",
      ]},
      { type: "tips", items: [
        "Make sure the customer's phone number is saved on their profile for SMS receipts to work.",
        "Termii SMS credits are billed separately by Termii — RetailPulse does not charge for SMS.",
      ]},
    ],
  },

  // ── INVENTORY ────────────────────────────────────────────────────────────────
  {
    id: "inv-add-product",
    category: "Inventory",
    title: "Adding and managing products",
    description: "How to add products, set prices, track stock, and organise your catalogue.",
    readTime: "4 min",
    popular: true,
    content: [
      { type: "p", text: "Your product catalogue lives in the Inventory section. Every product you sell needs to be added here before it can appear in the POS." },
      { type: "h2", text: "Adding a product" },
      { type: "steps", items: [
        "Go to Inventory and click Add Product.",
        "Enter the product name.",
        "Set the selling price (what the customer pays).",
        "Set the cost price (what you paid for it) — this powers profit calculations.",
        "Enter the opening stock quantity.",
        "Optionally assign a category, brand, unit of measurement, and barcode.",
        "Click Save.",
      ]},
      { type: "h2", text: "Low-stock alerts" },
      { type: "p", text: "Set a Low Stock Threshold on each product. When stock falls below this number, a warning appears on your dashboard and the product is highlighted in inventory." },
      { type: "h2", text: "Service items" },
      { type: "p", text: "For services (e.g. haircut, repair fee), tick the 'Is Service' checkbox. Service items have no stock count — they can be sold unlimited times without affecting inventory numbers." },
      { type: "h2", text: "Editing and deactivating" },
      { type: "list", items: [
        "Click any product in the list to edit its details.",
        "To hide a product from the POS without deleting it, set it as inactive using the status toggle.",
        "Deleted products are removed from the catalogue but their historical sales data is preserved.",
      ]},
      { type: "tips", items: [
        "Use categories to group products (e.g. Beverages, Snacks) — this makes filtering in reports much faster.",
        "The SKU field is optional but helpful if you use a barcode scanner.",
      ]},
    ],
  },
  {
    id: "inv-purchases",
    category: "Inventory",
    title: "Recording stock purchases",
    description: "How to log stock deliveries from suppliers and keep your inventory counts accurate.",
    readTime: "3 min",
    content: [
      { type: "p", text: "When you receive new stock from a supplier, record it as a Purchase. This increases your stock count and records the spend as a business cost." },
      { type: "h2", text: "Creating a purchase order" },
      { type: "steps", items: [
        "Go to Purchases and click New Purchase.",
        "Select the supplier (or add a new one).",
        "Add each product you received, the quantity, and the unit cost.",
        "Enter the purchase date and any reference number (e.g. supplier invoice number).",
        "Click Confirm Purchase.",
      ]},
      { type: "p", text: "Stock levels update automatically — no need to manually adjust inventory." },
      { type: "h2", text: "What about suppliers?" },
      { type: "p", text: "Suppliers are managed separately under Suppliers. You can add contact details, track what you've ordered from each supplier, and see outstanding balances in the Supplier Ledger." },
      { type: "note", text: "Purchases are reflected in your P&L report as Cost of Goods Sold. Make sure you record all deliveries to keep your profit figures accurate." },
    ],
  },
  {
    id: "inv-import",
    category: "Inventory",
    title: "Importing products in bulk",
    description: "How to upload your entire product list via CSV instead of adding them one by one.",
    readTime: "3 min",
    content: [
      { type: "p", text: "If you have a large catalogue, importing via CSV is much faster than adding products one by one." },
      { type: "h2", text: "How to import" },
      { type: "steps", items: [
        "Go to Inventory and click Import.",
        "Download the CSV template from the import page.",
        "Open the template in Excel or Google Sheets.",
        "Fill in your product data — name, cost price, selling price, stock quantity, category (optional), and barcode (optional).",
        "Save the file as CSV.",
        "Upload it on the import page and click Import.",
        "Review the preview — any errors will be highlighted row by row.",
        "Confirm to import all valid rows.",
      ]},
      { type: "tips", items: [
        "Leave the ID column blank — it's auto-assigned.",
        "Category names in your CSV will be created automatically if they don't already exist.",
        "Run a test import with 5–10 products first to confirm your format is correct.",
      ]},
      { type: "warning", text: "Do not change the column headers in the template — the system matches data by column name." },
    ],
  },

  // ── RETURNS & REFUNDS ────────────────────────────────────────────────────────
  {
    id: "ret-process",
    category: "Returns & Refunds",
    title: "Processing a customer return",
    description: "How to handle full and partial returns, select specific items, and restore stock.",
    readTime: "4 min",
    popular: true,
    content: [
      { type: "p", text: "RetailPulse supports partial returns — a customer can return one item from a three-item sale without affecting the rest." },
      { type: "h2", text: "Processing a return" },
      { type: "steps", items: [
        "Go to Returns and click Process Return.",
        "Search for the original receipt number using the search box (e.g. RCP-00042).",
        "Select the sale from the results.",
        "A list of items from that sale appears — tick the items being returned.",
        "Adjust the quantity for each selected item if the customer is only returning part of what they bought.",
        "The refund amount calculates automatically from the selected items.",
        "Choose the return type: Refund (cash back) or Exchange (swapping for another item).",
        "Enter the reason (e.g. Defective product, Wrong size).",
        "Click Process Refund / Process Exchange.",
      ]},
      { type: "h2", text: "What happens automatically" },
      { type: "list", items: [
        "Stock for the returned items is added back to inventory immediately.",
        "For refunds — loyalty points earned on the returned amount are deducted from the customer's balance.",
        "For exchanges — loyalty points are kept (no cash was returned). Ring up the replacement item as a new sale.",
        "The return is recorded in your Reports with the refund amount deducted from net revenue.",
      ]},
      { type: "h2", text: "Exchanges — the full flow" },
      { type: "p", text: "When a customer swaps an item, select Exchange as the return type. This restores the returned item's stock. Then process the replacement item as a brand new sale at the POS." },
      { type: "tips", items: [
        "Always search by receipt number — it's the fastest way to find the exact sale.",
        "Add a detailed reason — it appears in your returns report and helps spot patterns (e.g. recurring defects).",
      ]},
    ],
  },
  {
    id: "ret-reports",
    category: "Returns & Refunds",
    title: "How returns affect your reports",
    description: "Understanding how refunds impact revenue, profit, and inventory in your reports.",
    readTime: "2 min",
    content: [
      { type: "p", text: "Returns are tracked separately and deducted from your net figures automatically — you don't need to manually adjust anything." },
      { type: "h2", text: "Impact on reports" },
      { type: "table", headers: ["Report", "How returns affect it"], rows: [
        ["Revenue", "Return amount is deducted from net sales revenue"],
        ["P&L", "Refunded items reduce COGS and revenue proportionally"],
        ["Inventory", "Returned item stock is restored immediately"],
        ["Loyalty Points", "Points earned on refunded amount are deducted (refunds only)"],
      ]},
      { type: "note", text: "Exchanges do not reduce your revenue — they swap one item for another. Only refunds (cash returned to the customer) reduce net revenue." },
    ],
  },

  // ── BILLING ──────────────────────────────────────────────────────────────────
  {
    id: "bill-plans",
    category: "Billing & Plans",
    title: "Plan comparison and choosing the right plan",
    description: "What's included in Basic, Pro, and Enterprise — and how to decide which is right for you.",
    readTime: "4 min",
    popular: true,
    content: [
      { type: "p", text: "RetailPulse has three monthly plans. You can upgrade or downgrade at any time." },
      { type: "h2", text: "Plans at a glance" },
      { type: "table", headers: ["Feature", "Basic ₦7,500/mo", "Pro ₦12,000/mo", "Enterprise ₦28,000/mo"], rows: [
        ["Products", "Up to 1,500", "Up to 2,500", "Unlimited"],
        ["Staff accounts", "1", "5", "Unlimited"],
        ["Sales & POS", "✅", "✅", "✅"],
        ["Inventory management", "✅", "✅", "✅"],
        ["Purchase orders", "✅", "✅", "✅"],
        ["Expenses tracking", "✅", "✅", "✅"],
        ["Reports & P&L", "✅", "✅", "✅"],
        ["Report export (Excel/PDF)", "❌", "✅", "✅"],
        ["Loyalty programme", "❌", "✅", "✅"],
        ["Attendance tracking", "❌", "❌", "✅"],
        ["Advanced analytics", "❌", "❌", "✅"],
        ["Priority support", "❌", "❌", "✅"],
      ]},
      { type: "h2", text: "Which plan should I choose?" },
      { type: "list", items: [
        "Solo trader or small shop with one cashier → Basic.",
        "Growing business with staff, needs loyalty and export → Pro.",
        "Multi-staff operation, needs attendance and full analytics → Enterprise.",
      ]},
      { type: "tips", items: [
        "Start on Basic and upgrade when you need more — your data carries over instantly.",
        "Enterprise is ideal for fashion boutiques, salons, and supermarkets with 3+ staff.",
      ]},
    ],
  },
  {
    id: "bill-subscribe",
    category: "Billing & Plans",
    title: "How to subscribe and manage your billing",
    description: "Subscribing via Paystack, auto-renewal, cancellation, and refunds.",
    readTime: "3 min",
    content: [
      { type: "p", text: "Payments are processed securely via Paystack — Nigeria's leading payment processor." },
      { type: "h2", text: "Subscribing" },
      { type: "steps", items: [
        "Go to Subscribe from the sidebar or your account page.",
        "Choose a plan.",
        "Click Subscribe Now — you'll be redirected to a Paystack checkout page.",
        "Enter your card details (Visa, Mastercard, or Verve).",
        "Payment is charged immediately and your plan activates straight away.",
      ]},
      { type: "h2", text: "Auto-renewal" },
      { type: "p", text: "Your subscription renews automatically on the same date each month. You'll receive an email confirmation each time. If a payment fails, you'll get an alert and have 3 days to update your card before access is paused." },
      { type: "h2", text: "Cancellation" },
      { type: "p", text: "Cancel anytime from Settings → Billing or by messaging us on WhatsApp. You keep full access until the end of the period you've paid for — we don't cut access immediately." },
      { type: "h2", text: "Refunds" },
      { type: "p", text: "Refunds are available for billing errors, duplicate charges, or within 48 hours of your first payment if you've made fewer than 10 sales. See the full Refund Policy at retailpulsepos.com/refund-policy." },
    ],
  },

  // ── REPORTS ──────────────────────────────────────────────────────────────────
  {
    id: "rep-overview",
    category: "Reports",
    title: "Understanding your reports",
    description: "A guide to every report in RetailPulse and what you can learn from each one.",
    readTime: "5 min",
    content: [
      { type: "p", text: "Reports give you the numbers behind your business — what's selling, what you're spending, and whether you're profitable." },
      { type: "h2", text: "Sales Summary" },
      { type: "p", text: "Found under Reports. Shows total revenue, number of transactions, average order value, and payment method breakdown by day, week, or month." },
      { type: "h2", text: "Profit & Loss (P&L)" },
      { type: "p", text: "Found under Accounting → Profit & Loss. Shows Revenue − Cost of Goods Sold − Expenses = Net Profit. This is your bottom line. Run it monthly to understand your true profitability." },
      { type: "h2", text: "Expense Reports" },
      { type: "p", text: "Found under Accounting → Expense Reports. Breaks down every expense by category (rent, fuel, salaries, etc.) so you can see where your money goes." },
      { type: "h2", text: "Top Products" },
      { type: "p", text: "Shown on your dashboard and in Reports. Ranks products by units sold or by revenue generated. Use this to identify what to restock urgently and what to discount or discontinue." },
      { type: "h2", text: "Staff Performance" },
      { type: "p", text: "Found under People → Staff Performance. Shows sales by staff member — total sales count and revenue attributed to each person. Available on Pro and Enterprise plans." },
      { type: "h2", text: "Customer Reports" },
      { type: "p", text: "Found under People → Customer Reports. Shows your top customers by spend, loyalty points, and purchase frequency." },
      { type: "tips", items: [
        "Run your P&L at the end of every month and compare it to the previous month.",
        "If a product appears in Top Products but has a thin margin (low cost vs price gap), consider repricing.",
        "Export reports as Excel or PDF on Pro and Enterprise plans — useful for your accountant.",
      ]},
    ],
  },
  {
    id: "rep-profit",
    category: "Reports",
    title: "How profit is calculated",
    description: "Understanding gross profit, COGS, and what makes your P&L accurate or inaccurate.",
    readTime: "3 min",
    content: [
      { type: "p", text: "Accurate profit figures depend entirely on accurate cost prices. If your cost prices are wrong, your P&L will be wrong." },
      { type: "h2", text: "The formula" },
      { type: "list", items: [
        "Gross Profit = Total Sales Revenue − Cost of Goods Sold (COGS)",
        "COGS = Sum of (cost price × units sold) for every product",
        "Net Profit = Gross Profit − Total Expenses",
      ]},
      { type: "h2", text: "Common mistakes that break your P&L" },
      { type: "list", items: [
        "Leaving cost price blank — product shows 100% margin (impossible).",
        "Setting cost price to the retail price — gross profit will show as zero.",
        "Not recording expenses — net profit looks higher than it actually is.",
        "Not recording stock purchases — COGS is understated.",
      ]},
      { type: "tips", items: [
        "Update cost prices whenever your supplier prices change.",
        "Log every business expense in the Expenses section — even small ones like generator fuel — for an accurate picture.",
      ]},
    ],
  },

  // ── SETTINGS ─────────────────────────────────────────────────────────────────
  {
    id: "set-loyalty",
    category: "Settings",
    title: "Setting up the loyalty programme",
    description: "How to configure loyalty points, earn rates, and reward returning customers.",
    readTime: "3 min",
    content: [
      { type: "p", text: "The loyalty programme lets you reward customers with points on every purchase. Points can be redeemed for discounts on future sales. Available on Pro and Enterprise plans." },
      { type: "h2", text: "How points work" },
      { type: "p", text: "For every ₦100 spent, the customer earns 1 point (or more, depending on your earn rate setting). When they accumulate enough points, you can apply a discount at the POS." },
      { type: "h2", text: "Configuring the earn rate" },
      { type: "steps", items: [
        "Go to Settings.",
        "Find the Loyalty section.",
        "Set the Earn Rate (default is 1 point per ₦100).",
        "Click Save.",
      ]},
      { type: "h2", text: "Awarding points" },
      { type: "p", text: "Points are awarded automatically when you link a customer to a sale before confirming. The customer must have a profile in Customers for points to track." },
      { type: "h2", text: "Viewing a customer's points" },
      { type: "steps", items: [
        "Go to People → Loyalty.",
        "Search for the customer.",
        "Their point balance, tier, and transaction history are shown.",
      ]},
      { type: "note", text: "If a customer makes a return or refund, their loyalty points for that transaction are deducted automatically." },
    ],
  },
  {
    id: "set-sms",
    category: "Settings",
    title: "Setting up SMS receipts and alerts",
    description: "Connecting your Termii account to send SMS receipts and low-stock alerts to customers.",
    readTime: "3 min",
    content: [
      { type: "p", text: "RetailPulse integrates with Termii to send SMS receipts to customers and low-stock alerts to you. You need a Termii account — it's free to sign up and you buy SMS credits as needed." },
      { type: "h2", text: "Getting started with Termii" },
      { type: "steps", items: [
        "Go to termii.com and create a free account.",
        "Verify your account and purchase some SMS credits (they're very affordable).",
        "In your Termii dashboard, copy your API Key.",
      ]},
      { type: "h2", text: "Connecting Termii to RetailPulse" },
      { type: "steps", items: [
        "In RetailPulse, go to Settings → SMS.",
        "Paste your Termii API Key.",
        "Enter a Sender ID — this is the name that appears as the sender (e.g. RETAILPULSE or your store name).",
        "Click Save and Test to send a test SMS to your own number.",
      ]},
      { type: "tips", items: [
        "Sender IDs must be approved by Termii before they work — allow 24–48 hours for a custom sender ID to activate.",
        "Use a generic sender like RETAILPULS (max 11 chars) to start immediately while your custom ID is reviewed.",
      ]},
    ],
  },
  {
    id: "set-password",
    category: "Settings",
    title: "Resetting a forgotten password",
    description: "What to do if you or a staff member can't log in.",
    readTime: "2 min",
    content: [
      { type: "p", text: "If you've forgotten your password, you can reset it from the login page without contacting support." },
      { type: "h2", text: "Resetting your own password" },
      { type: "steps", items: [
        "Go to the login page and click Forgot Password.",
        "Enter the email address linked to your account.",
        "Click Send Reset Link.",
        "Check your email inbox (also check spam/junk).",
        "Click the link in the email — it's valid for 1 hour.",
        "Enter your new password and confirm it.",
        "Log in with your new password.",
      ]},
      { type: "h2", text: "Resetting a staff member's password" },
      { type: "p", text: "Admins can reset any staff member's password directly. Go to Staff, click the staff member's name, and click Reset Password. They'll receive an email with a reset link." },
      { type: "tips", items: [
        "If the reset email doesn't arrive within 5 minutes, check spam or message us on WhatsApp.",
        "For security, staff should use a unique password not used on other sites.",
      ]},
    ],
  },
];

// ─── Categories Config ────────────────────────────────────────────────────────

const CATEGORIES = [
  { label: "Getting Started", icon: Zap,           color: "text-blue-600",   bg: "bg-blue-50"   },
  { label: "Sales & POS",     icon: ShoppingCart,   color: "text-green-600",  bg: "bg-green-50"  },
  { label: "Inventory",       icon: Package,        color: "text-orange-600", bg: "bg-orange-50" },
  { label: "Returns & Refunds",icon: RefreshCcw,   color: "text-red-600",    bg: "bg-red-50"    },
  { label: "Billing & Plans", icon: CreditCard,     color: "text-purple-600", bg: "bg-purple-50" },
  { label: "Reports",         icon: BarChart3,      color: "text-teal-600",   bg: "bg-teal-50"   },
  { label: "Settings",        icon: Settings,       color: "text-gray-600",   bg: "bg-gray-100"  },
];

// ─── Render article content ───────────────────────────────────────────────────

function RenderSection({ s }: { s: Section }) {
  switch (s.type) {
    case "p":
      return <p className="text-muted-foreground leading-relaxed">{s.text}</p>;
    case "h2":
      return <h2 className="text-lg font-semibold mt-6 mb-2">{s.text}</h2>;
    case "h3":
      return <h3 className="font-semibold mt-4 mb-1">{s.text}</h3>;
    case "steps":
      return (
        <ol className="space-y-2 my-3">
          {s.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm text-muted-foreground">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              <span className="leading-relaxed pt-0.5">{item}</span>
            </li>
          ))}
        </ol>
      );
    case "list":
      return (
        <ul className="space-y-1.5 my-3">
          {s.items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "tips":
      return (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 my-4 space-y-2">
          <div className="flex items-center gap-2 text-primary font-medium text-sm">
            <Lightbulb className="w-4 h-4" /> Tips
          </div>
          {s.items.map((t, i) => (
            <p key={i} className="text-sm text-muted-foreground pl-6">{t}</p>
          ))}
        </div>
      );
    case "warning":
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 my-4 flex gap-3">
          <AlertCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
          <p className="text-sm text-orange-800">{s.text}</p>
        </div>
      );
    case "note":
      return (
        <div className="bg-muted rounded-lg p-4 my-4 flex gap-3">
          <HelpCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">{s.text}</p>
        </div>
      );
    case "table":
      return (
        <div className="overflow-x-auto my-4 rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60">
                {s.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2.5 text-left font-medium text-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.rows.map((row, ri) => (
                <tr key={ri} className="border-t">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-2.5 text-muted-foreground ${ci === 0 ? "font-medium text-foreground" : ""}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Help() {
  const [search, setSearch]           = useState("");
  const [activeCategory, setCategory] = useState<string | null>(null);
  const [openArticle, setOpenArticle] = useState<Article | null>(null);

  const popular  = ARTICLES.filter(a => a.popular);
  const filtered = ARTICLES.filter(a => {
    const matchCat    = !activeCategory || a.category === activeCategory;
    const q           = search.toLowerCase();
    const matchSearch = !q || a.title.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.category.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  // ── Article view ──────────────────────────────────────────────────────────
  if (openArticle) {
    const cat = CATEGORIES.find(c => c.label === openArticle.category);
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
            <button onClick={() => setOpenArticle(null)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Knowledge Base
            </button>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <div className="mb-6">
            <Badge className={`mb-3 ${cat?.bg ?? ""} ${cat?.color ?? ""} border-0`}>{openArticle.category}</Badge>
            <h1 className="text-3xl font-bold tracking-tight mb-3">{openArticle.title}</h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {openArticle.readTime} read</span>
            </div>
          </div>

          <div className="prose-like space-y-3">
            {openArticle.content.map((s, i) => <RenderSection key={i} s={s} />)}
          </div>

          <div className="mt-12 border-t pt-8">
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
              <MessageCircle className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="font-semibold mb-1">Still have questions?</p>
              <p className="text-sm text-muted-foreground mb-4">Chat with us on WhatsApp — we usually reply within a few hours.</p>
              <a href="https://wa.me/2349040460813?text=Hi%2C%20I%20need%20help%20with%20RetailPulse" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-2"><MessageCircle className="w-4 h-4" /> Chat on WhatsApp</Button>
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Hub view ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> RetailPulse Help
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Start free trial</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <BookOpen className="w-3 h-3 mr-1" /> Knowledge Base
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-4">How can we help?</h1>
          <p className="text-muted-foreground mb-8 text-lg">
            Step-by-step guides, tips, and answers for every part of RetailPulse.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search articles..."
              className="pl-10 h-12 text-base"
              value={search}
              onChange={e => { setSearch(e.target.value); setCategory(null); }}
            />
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-14">

        {/* Popular articles */}
        {!search && !activeCategory && (
          <section>
            <div className="flex items-center gap-2 mb-5">
              <Star className="w-4 h-4 text-amber-500" />
              <h2 className="font-semibold text-lg">Most popular</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {popular.map(a => (
                <button
                  key={a.id}
                  onClick={() => setOpenArticle(a)}
                  className="text-left border rounded-xl p-4 hover:border-primary/40 hover:bg-primary/3 transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{a.category}</p>
                      <p className="font-medium text-sm group-hover:text-primary transition-colors">{a.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" /> {a.readTime} read
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Category grid */}
        {!search && (
          <section>
            <h2 className="font-semibold text-lg mb-5">Browse by topic</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const count = ARTICLES.filter(a => a.category === cat.label).length;
                return (
                  <button
                    key={cat.label}
                    onClick={() => setCategory(activeCategory === cat.label ? null : cat.label)}
                    className={`border rounded-xl p-4 text-left transition-all hover:border-primary/40 group ${
                      activeCategory === cat.label ? "border-primary bg-primary/5" : ""
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg ${cat.bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-4.5 h-4.5 ${cat.color}`} />
                    </div>
                    <p className="font-medium text-sm">{cat.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{count} article{count !== 1 ? "s" : ""}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Article list */}
        <section>
          {(search || activeCategory) && (
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-lg">
                {activeCategory ? activeCategory : `Results for "${search}"`}
                <span className="text-muted-foreground font-normal text-base ml-2">({filtered.length})</span>
              </h2>
              {(search || activeCategory) && (
                <button
                  onClick={() => { setSearch(""); setCategory(null); }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear filter
                </button>
              )}
            </div>
          )}
          {!search && !activeCategory && (
            <h2 className="font-semibold text-lg mb-5">All articles</h2>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-16 border rounded-xl">
              <HelpCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-medium mb-1">No articles found</p>
              <p className="text-sm text-muted-foreground mb-5">Try a different search or browse by topic above.</p>
              <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="gap-2"><MessageCircle className="w-4 h-4" /> Ask us on WhatsApp</Button>
              </a>
            </div>
          ) : (
            <div className="divide-y border rounded-xl overflow-hidden">
              {filtered.map(a => {
                const cat = CATEGORIES.find(c => c.label === a.category);
                const Icon = cat?.icon ?? HelpCircle;
                return (
                  <button
                    key={a.id}
                    onClick={() => setOpenArticle(a)}
                    className="w-full text-left px-5 py-4 hover:bg-muted/40 transition-colors flex items-center gap-4 group"
                  >
                    <div className={`w-9 h-9 rounded-lg ${cat?.bg ?? "bg-muted"} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${cat?.color ?? "text-muted-foreground"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-sm group-hover:text-primary transition-colors">{a.title}</p>
                        {a.popular && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs px-1.5 py-0">Popular</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1">{a.description}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                      <span className="hidden sm:flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readTime}</span>
                      <ChevronRight className="w-4 h-4 group-hover:text-primary transition-colors" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Still need help */}
        <section className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <MessageCircle className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-2">Can't find what you're looking for?</h3>
          <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
            Our support team is on WhatsApp and usually responds within a few hours.
          </p>
          <a href="https://wa.me/2349040460813?text=Hi%2C%20I%20need%20help%20with%20RetailPulse" target="_blank" rel="noopener noreferrer">
            <Button className="gap-2"><MessageCircle className="w-4 h-4" /> Chat with us on WhatsApp</Button>
          </a>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
